// Monte Carlo simulation of process completion times.
//
// As of the agent-based refactor, the per-trial work happens in sim-engine.ts
// against a World built by sim-world.ts. This file keeps a couple of
// pure-math helpers (eligibility mask) and re-exports the higher-level Monte
// Carlo functions with the same signatures the rest of the app already calls.

import { generateWorld } from "./sim-world";
import {
  MODE_NAIVE,
  MODE_REROUTE,
  SimulateOnceResult,
  computeEligibleMask as engineComputeEligibleMask,
  simulateOnce as engineSimulateOnce,
} from "./sim-engine";
// Phase 2 of the React-engine port (docs/eng/pilot-react-engine.md):
// intake state moved to PilotContext + <IntakeSelect>. The bridge is
// the read-only surface for non-React callers like this one.
import { getIntake } from "./intake-bridge";
import type { Intake, ProcessDef } from "./processes";
import type { SimParams } from "./params";
import type { World } from "./sim-world";

// Re-exported so existing callers don't have to know the engine moved.
export const computeEligibleMask = engineComputeEligibleMask;

// --------------------------------------------------------------------------
// Web-Worker-backed async pipeline.
//
// The synchronous Monte Carlo functions still exist (and are used by callers
// that need a quick single-shot, e.g. portfolio rollup), but the sandbox's
// `runAndRender` now runs the heavy MC+winProb+breakdown work in a worker
// so the main thread stays responsive — knobs feel snappy, page scrolls
// immediately on process select, and a real progress bar is possible.
// --------------------------------------------------------------------------

export interface StepBreakdownEntry {
  label: string;
  from: string;
  to: string;
  avgHrs: number;
}

export interface MonteCarloResult {
  days: number[];
  median: number;
  p90: number;
  avgHumanH: number;
  failRate: number;
  n: number;
}

export interface RunAllResult {
  human: MonteCarloResult;
  ai: MonteCarloResult;
  winProb: number;
  representativeIdx: number;
  breakdown: {
    human: StepBreakdownEntry[];
    ai: StepBreakdownEntry[];
  };
}

export interface RunAllAsyncArgs {
  proc: ProcessDef;
  intake: Partial<Intake> | null | undefined;
  params: SimParams | Record<string, number>;
  n?: number;
  winProbN?: number;
  breakdownN?: number;
  onProgress?: (frac: number, done: number, total: number) => void;
}

interface SimHandler {
  onProgress?: (frac: number, done: number, total: number) => void;
  resolve: (data: RunAllResult) => void;
  reject: (err: Error) => void;
}

let _simWorker: Worker | null = null;
let _simRequestId = 0;
const _simHandlers = new Map<number, SimHandler>();

function ensureSimWorker(): Worker {
  if (_simWorker) return _simWorker;
  _simWorker = new Worker(new URL("./sim-worker.ts", import.meta.url), { type: "module" });
  _simWorker.addEventListener("message", (e: MessageEvent) => {
    const msg = e.data as
      | { type: "progress"; id: number; done: number; total: number }
      | { type: "result"; id: number; data: RunAllResult }
      | { type: "error"; id: number; error: string }
      | null
      | undefined;
    if (!msg || (msg as { id?: number }).id == null) return;
    const h = _simHandlers.get(msg.id);
    if (!h) return;
    if (msg.type === "progress") {
      h.onProgress?.(msg.done / msg.total, msg.done, msg.total);
    } else if (msg.type === "result") {
      _simHandlers.delete(msg.id);
      h.resolve(msg.data);
    } else if (msg.type === "error") {
      _simHandlers.delete(msg.id);
      h.reject(new Error(msg.error || "sim-worker error"));
    }
  });
  _simWorker.addEventListener("error", (e: ErrorEvent) => {
    // Worker-level error — fail every in-flight handler.
    _simHandlers.forEach(h => h.reject(new Error(e.message || "sim-worker crashed")));
    _simHandlers.clear();
  });
  return _simWorker;
}

/**
 * Run the full sandbox pipeline (human + AI MC, win probability, per-step
 * breakdown) in a Web Worker. Yields progress 0..1 via `onProgress`.
 *
 * Resolves with: { human, ai, winProb, representativeIdx, breakdown }
 */
export function runAllAsync(args: RunAllAsyncArgs): Promise<RunAllResult> {
  const id = ++_simRequestId;
  const worker = ensureSimWorker();
  // Cancel any previous in-flight request — only the newest one's result
  // is useful (the user moved the knob / picked a different process).
  _simHandlers.forEach((_h, oldId) => {
    worker.postMessage({ op: "cancel", id: oldId });
  });
  _simHandlers.clear();

  return new Promise<RunAllResult>((resolve, reject) => {
    _simHandlers.set(id, { onProgress: args.onProgress, resolve, reject });
    worker.postMessage({
      op: "runAll",
      id,
      proc: args.proc,
      intake: args.intake,
      params: args.params,
      n: args.n ?? 10000,
      winProbN: args.winProbN ?? 1500,
      breakdownN: args.breakdownN ?? 200,
    });
  });
}

// Legacy entrypoint, kept so callers (story / sandbox) don't have to change.
// Generates a one-trial world internally and runs the agent simulator.
//
// `useAI === true` → AI routes everywhere + handles AI-eligible steps.
// `useAI === false` → naive routing, no AI work.
export function simulateOnce(
  proc: ProcessDef,
  p: Record<string, number>,
  useAI: boolean,
  recordTrace = false,
): SimulateOnceResult {
  const intake = getIntake() as Partial<Intake>;
  // Each call rolls a fresh seed so successive calls produce different
  // trials (e.g. the lane animation refreshes on each replay).
  const seed = (Math.random() * 0x7fffffff) | 0;
  const world: World = generateWorld(proc, intake, p, seed);
  return engineSimulateOnce(world, useAI ? MODE_REROUTE : MODE_NAIVE, { recordTrace });
}


// ---------------------------------------------------------------------------
// Monte Carlo wrappers — agent-based world per trial.
//
// Each trial uses a deterministic seed (i) so paired comparisons (Planapus
// vs today) can replay the SAME world and ask "given identical reality, what
// did the routing/AI choice change?" This is the variance-reduction step.
// ---------------------------------------------------------------------------

function trialSeed(i: number, salt = 0): number {
  // Multiplied by a large odd → spreads adjacent seeds across the PRNG state.
  return (((i + 1) * 0x9E3779B1) ^ salt) | 0;
}

export function runMonteCarlo(
  proc: ProcessDef,
  p: Record<string, number>,
  n: number,
  useAI: boolean,
): MonteCarloResult {
  const intake = getIntake() as Partial<Intake>;
  const days: number[] = [];
  let humanH = 0;
  let failed = 0;
  const mode = useAI ? MODE_REROUTE : MODE_NAIVE;
  for (let i = 0; i < n; i++) {
    const world = generateWorld(proc, intake, p, trialSeed(i));
    const r = engineSimulateOnce(world, mode);
    // Defensive: only count finite, successful trials. A non-finite hours
    // value would propagate through Math.max(...days) and trigger an
    // unbounded loop in the histogram render.
    if (r.failed || !isFinite(r.hours)) { failed++; continue; }
    days.push(r.hours / 24);
    humanH += r.humanHours;
  }
  days.sort((a, b) => a - b);
  const pct = (q: number): number =>
    days.length ? days[Math.min(days.length - 1, Math.floor(q * days.length))] : 0;
  return {
    days,
    median: pct(0.5),
    p90: pct(0.9),
    avgHumanH: humanH / Math.max(1, days.length),
    failRate: failed / n,
    n,
  };
}

export interface MonteCarloSummary {
  avgHumanH: number;
  failRate: number;
}

export function runMonteCarloSummary(
  proc: ProcessDef,
  p: Record<string, number>,
  n: number,
  useAI: boolean,
): MonteCarloSummary {
  if (!proc._baselineMeanHrs) {
    proc._baselineMeanHrs = proc.steps.reduce((a, s) => a + s.baseHrs, 0) / proc.steps.length;
  }
  const intake = getIntake() as Partial<Intake>;
  const mode = useAI ? MODE_REROUTE : MODE_NAIVE;
  let humanH = 0;
  let succeeded = 0;
  let failed = 0;
  for (let i = 0; i < n; i++) {
    const world = generateWorld(proc, intake, p, trialSeed(i));
    const r = engineSimulateOnce(world, mode);
    if (r.failed) { failed++; continue; }
    humanH += r.humanHours;
    succeeded++;
  }
  return {
    avgHumanH: humanH / Math.max(1, succeeded),
    failRate: failed / n,
  };
}

export function sampleTrial(
  proc: ProcessDef,
  p: Record<string, number>,
  useAI: boolean,
): SimulateOnceResult {
  const intake = getIntake() as Partial<Intake>;
  const mode = useAI ? MODE_REROUTE : MODE_NAIVE;
  for (let i = 0; i < 30; i++) {
    const seed = (Math.random() * 0x7fffffff) | 0;
    const world = generateWorld(proc, intake, p, seed);
    const r = engineSimulateOnce(world, mode, { recordTrace: true });
    if (!r.failed && r.trace.length) return r;
  }
  // Couldn't find a non-failing trial in 30 tries — return whatever.
  const seed = (Math.random() * 0x7fffffff) | 0;
  const world = generateWorld(proc, intake, p, seed);
  return engineSimulateOnce(world, mode, { recordTrace: true });
}

export interface ReplayResult {
  human: SimulateOnceResult;
  ai: SimulateOnceResult;
  world: World;
}

/**
 * Replay a specific trial index (1..N) deterministically against the shared
 * world. Used by the sandbox + story lane animations so the user can scrub
 * through any of the 10,000 simulations behind the histogram.
 *
 * Both human and AI traces come from the SAME world (CRN) — what you see
 * is exactly what the head-to-head comparison saw.
 */
export function replayTrial(
  proc: ProcessDef,
  p: Record<string, number>,
  trialIdx: number,
): ReplayResult {
  const intake = getIntake() as Partial<Intake>;
  // trialIdx is 1-indexed in the UI; trialSeed expects 0-indexed.
  const world = generateWorld(proc, intake, p, trialSeed(Math.max(0, trialIdx - 1)));
  const human = engineSimulateOnce(world, MODE_NAIVE, { recordTrace: true });
  const ai    = engineSimulateOnce(world, MODE_REROUTE, { recordTrace: true });
  return { human, ai, world };
}

// Per-step latency profile — runs n trials, accumulates each step's wall-
// clock latency, and returns the mean per step. Used to render the "where
// the time goes" breakdown so the user can see WHICH handoffs are dragging.
export function runStepBreakdown(
  proc: ProcessDef,
  p: Record<string, number>,
  n: number,
  useAI: boolean,
): StepBreakdownEntry[] {
  const intake = getIntake() as Partial<Intake>;
  const mode = useAI ? MODE_REROUTE : MODE_NAIVE;
  const totals = new Array<number>(proc.steps.length).fill(0);
  const counts = new Array<number>(proc.steps.length).fill(0);
  for (let i = 0; i < n; i++) {
    const world = generateWorld(proc, intake, p, trialSeed(i));
    const r = engineSimulateOnce(world, mode, { recordTrace: true });
    for (const t of r.trace) {
      const idx = proc.steps.findIndex(s => s.label === t.label);
      if (idx >= 0) { totals[idx] += t.lat; counts[idx]++; }
    }
  }
  return proc.steps.map((step, i) => ({
    label: step.label,
    from: step.from,
    to: step.to,
    avgHrs: counts[i] ? totals[i] / counts[i] : 0,
  }));
}

export interface WinProbabilityResult {
  winProb: number;
  representativeIdx: number;
}

/**
 * Paired trials with common random numbers. Returns:
 *   - winProb: fraction where Planapus finishes faster than today
 *   - representativeIdx: 1-indexed trial whose speedup is closest to the
 *     median across all AI-wins-and-both-finished pairs. The sandbox uses
 *     this as the default lane animation so the user lands on a typical
 *     run (not a tail-case where the two paths happen to tie or AI is
 *     slightly slower).
 */
export function runWinProbability(
  proc: ProcessDef,
  p: Record<string, number>,
  n: number,
): WinProbabilityResult {
  const intake = getIntake() as Partial<Intake>;
  let aiWins = 0;
  let valid = 0;
  const winRatios: Array<{ idx: number; ratio: number }> = [];
  for (let i = 0; i < n; i++) {
    const world = generateWorld(proc, intake, p, trialSeed(i));
    const h = engineSimulateOnce(world, MODE_NAIVE);
    const a = engineSimulateOnce(world, MODE_REROUTE);
    if (h.failed || a.failed || !isFinite(h.hours) || !isFinite(a.hours)) continue;
    valid++;
    // Strict < — ties are parity, not improvement. Headline reads
    // "Planapus improves X% of cases".
    if (a.hours < h.hours) {
      aiWins++;
      winRatios.push({ idx: i, ratio: a.hours / Math.max(0.001, h.hours) });
    }
  }
  winRatios.sort((x, y) => x.ratio - y.ratio);
  const medianWin = winRatios.length ? winRatios[Math.floor(winRatios.length / 2)] : null;
  return {
    winProb: valid ? aiWins / valid : 0,
    // 1-indexed for the UI slider; fall back to trial 1 if no wins exist.
    representativeIdx: medianWin ? medianWin.idx + 1 : 1,
  };
}
